import http from 'apis/config/http-service';
import { useMemo } from 'react';
import { useQueries } from 'react-query';
import type { Paginated, QuotationSupplierProduct } from './types';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// GET .../quotation-suppliers/:supplierId/products for a SET of suppliers,
// indexed by productId. The match matrix uses this to enrich a quote with the
// origin/state/specs of the exact product that was quoted — the quote's own
// product summary doesn't carry those fields yet (flagged BE gap), but the
// supplier-catalog row for the same productId does. Real data for the same
// product, with one caveat: it reflects the catalog as of the last fetch, not
// a snapshot taken at quote time — refetchOnWindowFocus below (a deliberate
// per-query override of the app-wide `false`) picks up edits made in the
// Exclusive Deals tab when ops switches back to this one.
const quotationSuppliersEndpoint = `${restaurantInquiriesEndpoint}/quotation-suppliers`;

// 100 is the endpoint's hard max (limit=101 → 400, verified on staging).
// Deal-gated supplier catalogs are small, so one page covers them; if a
// supplier ever exceeds it, the missed products simply stay un-enriched
// ('—' cells, excluded from the score) — never wrong, just absent.
const PAGE_LIMIT = 100;

export function useQuotationSupplierProductIndexApi(supplierIds: number[]): Map<number, QuotationSupplierProduct> {
  const results = useQueries(
    supplierIds.map((sid) => ({
      queryKey: [quotationSuppliersEndpoint, sid, 'products', { limit: PAGE_LIMIT }],
      queryFn: async (): Promise<Paginated<QuotationSupplierProduct>> => {
        const response = await http.get(`${quotationSuppliersEndpoint}/${sid}/products`, {
          params: { page: 1, limit: PAGE_LIMIT },
        });
        return response.data;
      },
      staleTime: 30_000,
      refetchOnWindowFocus: true,
    }))
  );

  // Rebuild only when a fetch actually lands (dataUpdatedAt changes) — the
  // `results` array identity changes every render, so it can't be the dep.
  const updatedKey = results.map((r) => r.dataUpdatedAt).join(',');

  return useMemo(() => {
    const index = new Map<number, QuotationSupplierProduct>();
    results.forEach((r) => {
      (r.data as Paginated<QuotationSupplierProduct> | undefined)?.data?.forEach((p) => index.set(p.id, p));
    });
    return index;
  }, [updatedKey]);
}
